import type { Nullable } from 'primereact/ts-helpers';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { ApiBaseResponse, StoreBaseResponse } from '@/types/types';
import type { AxiosResponse } from 'axios';
import { checkIsError } from './api-utils';
import type z from 'zod';
import i18n from '@/config/i18nConfig';
import {
  constants,
  currencyLocaleMap,
  imageExtensions,
  videoExtensions,
} from '@/constants/constants';
import QRCode from 'qrcode';
import CryptoJS from 'crypto-js';
import { zstr } from '../zodLib';
import { nonTT } from '@/constants/shipping-constants';
import { goToViewCarrier } from './links/carriersLinks';
import { goToViewCustomer } from './links/customerLinks';
import { goToViewPayment } from './links/paymentCreditLinks';

export function processTitleCase(
  str: Nullable<string>,
  separator: string = ' ',
) {
  if (!str) return '';
  return str
    .split(separator)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
export function processCurrency(
  amount: Nullable<number | string> = 0,
  currency: string,
  type?: 'symbol' | 'value' | 'input' | 'bracket-within',
  locale?: string,
  minimumFractionDigits?: number,
  compact?: boolean,
) {
  // eg output structure
  //   [
  //   {
  //     "type": "currency",
  //     "value": "￥"
  //   },
  //   {
  //     "type": "integer",
  //     "value": "3"
  //   },
  //   {
  //     "type": "group",
  //     "value": ","
  //   },
  //   {
  //     "type": "integer",
  //     "value": "784"
  //   },
  //   {
  //     "type": "group",
  //     "value": ","
  //   },
  //   {
  //     "type": "integer",
  //     "value": "911"
  //   },
  //   {
  //     "type": "decimal",
  //     "value": "."
  //   },
  //   {
  //     "type": "fraction",
  //     "value": "00"
  //   }
  // ]
  const _amount = convertNumber(amount);
  if (!currency) return '';
  if (currency?.toLowerCase() === 'yen') currency = 'JPY';
  if (Number.isNaN(_amount)) return '';
  if (minimumFractionDigits) {
    // do nothing
  }
  const parts = Intl.NumberFormat(locale || currencyLocaleMap[currency], {
    style: 'currency',
    currency,
    // minimumFractionDigits:
    //   type === 'input' ? undefined : (minimumFractionDigits ?? 2),
    minimumFractionDigits: 0,
    notation: compact ? 'compact' : undefined,
  }).formatToParts(_amount);

  if (type === 'symbol') {
    return parts[0].value;
  }

  if (type === 'value' || type === 'input') {
    return parts
      .map((part) => {
        if (part.type === 'currency') {
          return '';
        }
        return part.value;
      })
      .join('');
  }

  if (type == 'bracket-within') {
    return (
      parts[0].value +
      ' ' +
      `(${parts
        .map((part) => {
          if (part.type === 'currency') {
            return;
          }
          return part.value;
        })
        .join('')})`
    );
  }

  return parts
    .map((part) => {
      if (part.type === 'currency') {
        return part.value + ' ';
      }
      return part.value;
    })
    .join('');
}

export function processCurrencyNoUnit(
  amount: Nullable<number | string> = 0,
  currency: string,
  type?: 'symbol' | 'value' | 'input',
  locale?: string,
  compact?: boolean,
) {
  return processCurrency(amount, currency, type, locale, 0, compact);
}
export function getCurrencySymbol(currency: string, locale?: string) {
  if (!currency) return '';
  if (currency?.toLowerCase() === 'yen') currency = 'JPY';
  const parts = Intl.NumberFormat(locale || currencyLocaleMap[currency], {
    style: 'currency',
    currency,
  }).formatToParts(0);

  return parts[0].value;
}
export const generateRandomPassword = () => {
  const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*_=+-';
  const allChars = upperCase + lowerCase + numbers + symbols;

  const getRandomChar = (chars: string) =>
    chars[Math.floor(Math.random() * chars.length)];

  let password = '';
  password += getRandomChar(upperCase);
  password += getRandomChar(lowerCase);
  password += getRandomChar(symbols);
  password += getRandomChar(numbers);

  for (let i = 4; i < 8; i++) {
    password += getRandomChar(allChars);
  }

  return password;
};
export const objectNullify = <T>(
  obj: T,
  ignore?: string[],
  keepZero?: boolean,
) => {
  if (obj && typeof obj === 'object') {
    for (const key in obj) {
      if (
        (obj[key] === '' ||
          obj[key] === undefined ||
          (!keepZero && obj[key] === 0)) &&
        !ignore?.includes(key)
      ) {
        // @ts-expect-error generic
        obj[key] = null;
      }
    }
  }
  return obj;
};
export const processDecimal = (
  num: Nullable<number | string>,
  decimalLimit: number = 2,
) => {
  if (!num) return 0;

  if (String(num).includes('.')) return Number(num).toFixed(decimalLimit);

  num = Number(num);
  if (isNaN(num)) {
    return NaN;
  }

  return num.toFixed(decimalLimit || 0) || 0;
};

export const processUnitDecimal = (num: Nullable<number | string>) => {
  return processDecimal(num, 3);
};
export const setMinMax = (
  setter: (value: number) => void,
  value: Nullable<number>,
  max: number,
  min: number = 0,
) => {
  if (value) {
    if (value <= max && value >= min) {
      setter(value);
    } else if (value > max) {
      setter(max);
    } else if (value < min) {
      setter(min);
    }
  }
};
export const processWordLimit = (
  setter: unknown,
  value: string,
  limit: number = 300,
) => {
  if (typeof setter === 'function') {
    if ((value && value.length <= limit) || value === '') {
      setter(value);
    }
  }
};

export const cn = (...inputs: ClassValue[]): string => {
  return twMerge(clsx(inputs));
};
export function handleResult<T = unknown>(
  response: AxiosResponse<ApiBaseResponse<T>, unknown>,
) {
  const result: StoreBaseResponse<T> = {
    error: false,
    message: '',
  };
  result.message = response.data.message;
  result.status_code = response.data.status_code;
  if (checkIsError(response.data.error)) {
    result.error = true;
    return result;
  } else {
    result.data = response.data.data;
    return result;
  }
}
export const isJson = async (blob: Blob): Promise<boolean> => {
  try {
    // Read first few bytes to detect if it's JSON
    const text = await blob.text();
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

export function checkIsVehicleOrMachine(id: number) {
  //10 = vehicle, 20 = machine
  return [10, 20].includes(id);
}

export function checkRequired(
  field: unknown,
  path: string | string[],
  ctx: z.RefinementCtx,
  message: string = 'fieldRqd',
) {
  if (
    field === undefined ||
    field === null ||
    (typeof field === 'string' && field.trim() === '') ||
    (typeof field === 'number' && field === 0)
  ) {
    ctx.addIssue({
      path: typeof path === 'string' ? [path] : path,
      message: i18n.t(message),
      code: 'custom',
    });
  }
}

export function checkMax(
  field: unknown,
  path: string | string[],
  ctx: z.RefinementCtx,
  max: number = 0,
  message: string = 'fieldMax',
) {
  if (field && typeof field === 'string') {
    if (field.length > max) {
      ctx.addIssue({
        path: typeof path === 'string' ? [path] : path,
        message: i18n.t(message, { max }),
        code: 'custom',
      });
    }
  }

  if (field && typeof field === 'number') {
    if (field > max) {
      ctx.addIssue({
        path: typeof path === 'string' ? [path] : path,
        message: i18n.t(message, { max }),
        code: 'custom',
      });
    }
  }
}

export function checkLength(
  field: unknown,
  path: string | string[],
  ctx: z.RefinementCtx,
  min: number = 0,
  max: number = 0,
  message: string = 'fieldBetween',
) {
  if (field && typeof field === 'string') {
    if (field.length < min || field.length > max) {
      ctx.addIssue({
        path: typeof path === 'string' ? [path] : path,
        message: i18n.t(message, { min, max }),
        code: 'custom',
      });
    }
  }

  if (field && typeof field === 'number') {
    if (field < min || field > max) {
      ctx.addIssue({
        path: typeof path === 'string' ? [path] : path,
        message: i18n.t(message, { min, max }),
        code: 'custom',
      });
    }
  }
}

export function checkRegex(
  field: unknown,
  path: string | string[],
  ctx: z.RefinementCtx,
  message: string = 'invalidValue',
  regex: RegExp,
) {
  if (field && typeof field === 'string') {
    if (!regex.test(field)) {
      ctx.addIssue({
        path: typeof path === 'string' ? [path] : path,
        message: i18n.t(message),
        code: 'custom',
      });
    }
  }

  if (field && typeof field === 'number') {
    if (!regex.test(String(field))) {
      ctx.addIssue({
        path: typeof path === 'string' ? [path] : path,
        message: i18n.t(message),
        code: 'custom',
      });
    }
  }
}

export function convertStringFieldsToNumber<T extends object>(
  object: T,
  ignore: string[] = [],
): T {
  const temp: T = { ...object };
  Object.keys(temp).forEach((key) => {
    const _key = key as keyof T;
    if (!ignore?.includes(String(_key))) {
      if (!Number.isNaN(Number(temp[_key]) && temp[_key] !== '')) {
        (temp[_key] as number) = Number(temp[_key]);
      }
    }
  });
  return temp;
}

// Utility to map a number to a dark color
export function numberToDarkColor(num: number): string {
  // Step 1: Create a simple hash from the number
  const hash = Math.abs(Math.sin(num) * 10000);

  // Step 2: Derive hue from hash (0–360)
  const hue = Math.floor(hash % 360);

  // Step 3: Keep saturation high (60–100%) for colorful darks
  const saturation = 70;

  // Step 4: Force lightness to be dark (10–30%)
  const lightness = 20;

  // Return as HSL string (CSS supports this directly)
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export function processApiNumber(num: Nullable<string | number>) {
  if (num === null || num === undefined) return '';
  return String(convertNumber(num));
}

export async function imageUrlToBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`);
  }
  return await response.blob();
}

export function checkEmailValidity(email: Nullable<string>) {
  if (!email) return false;

  const emailSchema = zstr
    .email()
    .refine((value) => /\.(com|org|net|edu|gov|io|co|in)$/i.test(value), {
      message: 'Invalid email domain suffix',
    });

  return emailSchema.safeParse(email).success;
}

export function checkPhoneNoValidity(phoneNo: string) {
  const phoneRegex = constants.phoneRegex;
  return phoneRegex.test(phoneNo);
}

export function convertNumber(num: Nullable<string | number>) {
  if (!num) return 0;
  return Number(num) || 0;
}

export function generateValidString(
  value: Nullable<string | number>[],
  esc: string = ' ',
) {
  let str = '';
  const filteredValue = value.filter((a) => a && !!String(a)?.trim());
  filteredValue.forEach((item, i) => {
    const isLast = i === filteredValue.length - 1;
    if (String(value).toLowerCase() === 'null') return '';
    if (String(value).toLowerCase() === 'undefined') return '';
    if (item) {
      if (!isLast) {
        str += `${String(item)}${esc}`;
      } else {
        str += `${String(item)}`;
      }
    }
  });
  return str;
}

export const generateQRCodeDataURL = async (text: string) => {
  try {
    return await QRCode.toDataURL(text);
  } catch (err) {
    console.error('Error generating QR code:', err);
    return null;
  }
};

export function encryptString(str: string, secretKey?: string) {
  return CryptoJS.AES.encrypt(
    str,
    secretKey || import.meta.env.VITE_PDF_SECRET_KEY,
  ).toString();
}

export const decryptString = (
  encryptedStr: string,
  secretKey?: string,
): string => {
  const bytes = CryptoJS.AES.decrypt(
    encryptedStr,
    secretKey || import.meta.env.VITE_PDF_SECRET_KEY,
  );
  try {
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return '';
  }
};

export function printPdf(pdfFile: Blob | File) {
  const fileURL = URL.createObjectURL(pdfFile);

  // Create an invisible iframe
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = fileURL;

  document.body.appendChild(iframe);

  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
  };
}

export function isTT(value: Nullable<string>) {
  if (!value) return false;
  return !nonTT.includes(value?.toLowerCase());
}

export const shareFile = async (
  fileBlob: Blob,
  fileName: string,
  title: string = 'File',
  message: string = '',
) => {
  const file = new File([fileBlob], fileName, {
    type: 'application/pdf',
  });

  if (
    navigator.share &&
    navigator.canShare &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({
        title: title,
        text: message,
        files: [file],
      });
      return;
    } catch (err) {
      console.warn('Share cancelled or failed:', err);
    }
  }
};

export function randomNumber() {
  return Math.random().toString(36).substring(2, 15);
}

export function getUrlBasedOnType(type: string, id: Nullable<number | string>) {
  if (!id) return '';
  let url = '';
  if (type) {
    switch (type) {
      case 'carrier_id': {
        url = goToViewCarrier(Number(id));
        break;
      }
      case 'customer_id': {
        url = goToViewCustomer(Number(id));
        break;
      }
      case 'payment_id': {
        url = goToViewPayment(Number(id));
        break;
      }
    }
  }

  return url;
}

export function renderWithReplacements(
  text: string,
  replacements: Record<string, string | React.ReactNode>,
): React.ReactNode[] {
  const regex = /#(.*?)#/g;
  const parts: React.ReactNode[] = [];

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, key] = match;
    const start = match.index;

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    parts.push(replacements[key] ?? fullMatch);

    lastIndex = start + fullMatch.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

export function maskString(input: Nullable<string>, maskChar: string = '*') {
  if (!input) return '';
  if (input.length == 1) return input;
  if (input.length == 2) return input[0] + '*';
  if (input.length <= 4)
    return (
      input.slice(0, 1) +
      maskChar.repeat(convertNumber(input.length - 2)) +
      (input?.slice(-1) || '')
    );
  return (
    input.slice(0, 2) + maskChar.repeat(input.length - 4) + input.slice(-2)
  );
}

export function maskEmail(input: Nullable<string>, maskChar: string = '*') {
  if (!input) return '';
  const emailParts = input?.split('@') || [];
  return maskString(emailParts[0], maskChar) + '@' + emailParts[1];
}

export function maskJpPhone(input: Nullable<string>) {
  const str = input || '';
  if (str?.includes('-')) {
    const dashIndex = str.indexOf('-');

    const first = String(Number(str.slice(0, dashIndex)));
    const rest = str.slice(dashIndex + 1);

    const paddedFirst = first.padStart(3, '0');
    const restProcess = rest.split('-').join('').split('');
    const processedRest = [];
    let word = '';
    for (let i = 1; i <= restProcess.length; i++) {
      word += restProcess[i - 1];
      if (i % 4 === 0) {
        processedRest.push(word);
        word = '';
      }
    }
    if (word) {
      processedRest.push(word);
      word = '';
    }

    const phone = `${paddedFirst}-${processedRest.join('-')}`;
    return phone;
  }
  return input;
}

export function maskJpZipCode(input: Nullable<string>) {
  if (!input) return '';
  if (input.includes('-')) {
    return input;
  } else {
    return input.slice(0, 3) + '-' + input.slice(3);
  }
}

export type DataRecord = Record<
  string,
  string | number | boolean | null | undefined
>;

export function exportToCSV<T extends DataRecord>(
  data: T[],
  filename: string = 'export.csv',
): void {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const headers = Object.keys(data[0]);

  const escapeCSV = (value: unknown) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    // Escape quotes by doubling them, wrap in quotes if needed
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const csvRows = [
    headers.join(','), // header row
    ...data.map((row) =>
      headers.map((field) => escapeCSV(row[field])).join(','),
    ),
  ];

  const csvContent = csvRows.join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

export function isValidGtmId(gtmId: string) {
  //validate google tag manager container id
  return /^GTM-[A-Z0-9]+$/.test(gtmId);
}

export function sanitizeSocialHandle(input: string): string {
  const value = input.trim();

  try {
    const url = new URL(value);

    const split = url.pathname.split('/').filter(Boolean);
    const final = split[0];
    if (['in', 'company', 'school', 'pub'].includes(final)) {
      return generateValidString([split[0], split[1]], '/');
    } else {
      return final;
    }
  } catch {
    const split = value.split('/').filter(Boolean);
    const final = split[0];
    if (['in', 'company', 'school', 'pub'].includes(final) && split[1]) {
      return generateValidString([split[0], split[1]], '/');
    } else {
      return final || '';
    }
  }
}

export function isValidLineUserId(userId: Nullable<string>) {
  const lineUserRegex = /^[a-zA-Z0-9._-]+$/;
  return typeof userId === 'string' && lineUserRegex.test(userId);
}

export function downloadBlobUrl(blobUrl: string, filename = 'document.pdf') {
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function checkIsVideo(url: string) {
  return videoExtensions.some((ext) => url.toLowerCase().endsWith(ext));
}

export function checkIsImage(url: string) {
  return imageExtensions.some((ext) => url.toLowerCase().endsWith(ext));
}

export function sizeInBytes(num: Nullable<number | string>) {
  const size1KB = 1024;
  const size1MB = size1KB * size1KB;
  return convertNumber(num) * size1MB;
}
